import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

/**
 * Hook para gestionar permisos de módulos del usuario actual
 * @returns {Object} { modulos, loading, error, puedeVer, esAdmin }
 */
export const usePermisos = () => {
  const { user } = useAuth();
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [esAdmin, setEsAdmin] = useState(false);

  useEffect(() => {
    // Usar user.id o user._id (depende de cómo venga del AuthContext)
    const userId = user?.id || user?._id;
    
    if (!user || !userId) {
      setLoading(false);
      return;
    }

    const cargarPermisos = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/usuarios/${userId}/permisos`);
        setModulos(res.data.modulos_visibles || []);
        setEsAdmin(res.data.is_admin || false);
        setError(null);
      } catch (err) {
        console.error('❌ Error al cargar permisos:', err);
        console.error('   Usuario ID usado:', userId);
        console.error('   Error completo:', err.response?.data || err.message);
        setError(err);
        // En caso de error, asignar permisos mínimos según rol
        if (user.rol === 'admin') {
          setModulos([
            'dashboard', 'ordenes', 'presupuestos', 'clientes', 'equipos',
            'agendamientos', 'agendamientos_inteligentes', 'usuarios', 'panelAvanzado',
            'recepciones_equipos', 'entregas_documentos', 'movilidad'
          ]);
          setEsAdmin(true);
        } else if (user.rol === 'tecnico') {
          setModulos(['dashboard', 'ordenes', 'presupuestos', 'clientes', 'equipos', 'agendamientos',
            'recepciones_equipos', 'entregas_documentos', 'movilidad']);
        } else if (user.rol === 'cliente') {
          setModulos(['dashboard_cliente', 'mis_ordenes', 'mis_equipos', 'mis_agendamientos', 'mis_horas', 'monitor_servicios']); // Incluir por defecto en fallback
        } else {
          setModulos(['dashboard']);
        }
      } finally {
        setLoading(false);
      }
    };

    cargarPermisos();
  }, [user]);

  /**
   * Verifica si el usuario puede ver un módulo específico
   * @param {string} modulo - Nombre del módulo a verificar
   * @returns {boolean}
   */
  const puedeVer = (modulo) => {
    // Los admins siempre pueden ver todo
    if (esAdmin) return true;
    return modulos.includes(modulo);
  };

  return {
    modulos,
    loading,
    error,
    puedeVer,
    esAdmin
  };
};

export default usePermisos;
